<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import AttributeField from './AttributeField.vue';

const props = defineProps({
  customAttributes: {
    type: Object,
    default: () => ({}),
  },
  attributeDefinitions: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update', 'remove']);
const { t } = useI18n();

const selectedAttribute = ref(null);

const activeAttributeKeys = computed(() =>
  Object.keys(props.customAttributes || {})
);

const remainingDefinitions = computed(() => {
  return Object.entries(props.attributeDefinitions)
    .filter(([key]) => !activeAttributeKeys.value.includes(key))
    .map(([key, config]) => ({
      id: key,
      name: t(`SETTINGS.SPACES.ATTRIBUTES.${key.toUpperCase()}.LABEL`),
      icon: config.icon || 'i-lucide-tag',
    }));
});

const getDefinition = key => props.attributeDefinitions[key] || {};
const getLabel = key =>
  t(`SETTINGS.SPACES.ATTRIBUTES.${key.toUpperCase()}.LABEL`);
const getDescription = key =>
  t(`SETTINGS.SPACES.ATTRIBUTES.${key.toUpperCase()}.DESCRIPTION`);

watch(selectedAttribute, newAttr => {
  if (newAttr?.id) {
    const def = getDefinition(newAttr.id);
    let defaultValue;
    if (def.type === 'boolean') defaultValue = false;
    else if (def.type === 'number') defaultValue = 0;
    else if (def.type === 'list') defaultValue = def.options?.[0] || '';
    else defaultValue = '';
    emit('update', newAttr.id, defaultValue);
    selectedAttribute.value = null;
  }
});
</script>

<template>
  <div class="space-y-3 py-2">
    <!-- Active Attributes: 3-column grid -->
    <div
      v-if="activeAttributeKeys.length > 0"
      class="grid grid-cols-1 lg:grid-cols-2 gap-2"
    >
      <AttributeField
        v-for="key in activeAttributeKeys"
        :key="key"
        :attr-key="key"
        :value="customAttributes[key]"
        :definition="getDefinition(key)"
        :label="getLabel(key)"
        :description="getDescription(key)"
        @update="(k, v) => emit('update', k, v)"
        @remove="k => emit('remove', k)"
      />
    </div>

    <!-- Empty State -->
    <p v-else class="text-sm text-n-slate-9 px-3 py-2">
      {{ t('SETTINGS.SPACES.ATTRIBUTES.EMPTY') }}
    </p>

    <!-- Add Attribute Dropdown -->
    <div v-if="remainingDefinitions.length > 0">
      <SingleSelect
        v-model="selectedAttribute"
        :options="remainingDefinitions"
        :placeholder="t('SETTINGS.SPACES.ATTRIBUTES.ADD')"
        placeholder-icon="i-lucide-plus"
        :search-placeholder="t('SETTINGS.SPACES.ATTRIBUTES.SEARCH')"
        :disable-search="remainingDefinitions.length < 5"
        class="max-w-xs"
      />
    </div>
  </div>
</template>
